import React, { useMemo } from "react";

import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  IconButton,
  Table as MuiTable,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useAccountSelector } from "store";
import { Characteristic } from "store/types";

export interface Props {
  setCharacteristic: (item: Characteristic) => void;
  data: Characteristic[];
  onDelete: (id: number) => void;
}

const PROTECTED_CHARACTERISTICS = ["Currency", "Geography", "Vintage"];

const baseColumns: Array<{ key: keyof Characteristic; title: string }> = [
  { key: "name", title: "Characteristic" },
  { key: "category_name", title: "Category" },
  { key: "data_type", title: "Data Type" },
  { key: "is_global", title: "Is Global" },
  { key: "is_show", title: "Show" },
  { key: "is_multiple", title: "Multiple" },
  { key: "numerify", title: "Numerify" },
  { key: "postfix", title: "Postfix" },
  { key: "reduce_method", title: "Reduce Method" }
];

export const Table: React.FC<Props> = ({ setCharacteristic, data, onDelete }) => {
  const { single_asset_report } = useAccountSelector() || {};
  const columns = useMemo(() => {
    if (single_asset_report) {
      const columnsWithSubcategory = [...baseColumns];
      columnsWithSubcategory.splice(1, 0, { key: "subcategory_name", title: "Sub Category" });
      return columnsWithSubcategory;
    }
    return baseColumns;
  }, [single_asset_report]);

  return (
    <Stack
      sx={{
        p: "16px",
        borderRadius: "8px",
        overflow: "hidden",
        bgcolor: "white",
        boxShadow: "0 1px 2px 0 #1212171a",
        margin: "0 2px 2px 2px"
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "8px",
          boxShadow: "none",
          height: "100%",
          border: "1px solid #dae0e2",
          borderTop: "none"
        }}
      >
        <MuiTable
          stickyHeader
          sx={{
            "table-layout": "auto",
            ".MuiTableCell-root": {
              ":last-child": { width: "4%" },
              ":first-child": {
                minWidth: "200px",
                maxWidth: "200px",
                textAlign: "left"
              }
            }
          }}
        >
          <TableHead sx={{ bgcolor: "#edf1f9" }}>
            <TableRow sx={{ height: "44px" }}>
              {columns.map((col) => (
                <TableCell sx={commonHeadCellSx} key={col.key}>
                  {col.title}
                </TableCell>
              ))}
              <TableCell sx={commonHeadCellSx} />
            </TableRow>
          </TableHead>
          <TableBody className="characteristic-table__body">
            {data.map((row) => (
              <TableRow
                sx={{
                  height: "44px",
                  bgcolor: "#f5f7f7",
                  transition: "all 0.2s linear",
                  ".MuiButtonBase-root": { opacity: 0, transition: "all 0.3s linear" },
                  ":hover": { bgcolor: "#dae0e2", ".MuiButtonBase-root": { opacity: 1 } }
                }}
                className="characteristic-table__body-row"
                key={row.id}
              >
                {columns.map((col, index: number) => {
                  return (
                    <TableCell
                      sx={{ ...commonBodyCellSx, cursor: !index ? "pointer" : "default" }}
                      key={col.key + row.id}
                      {...(index === 0 && { onClick: () => setCharacteristic(row) })}
                    >
                      {row[col.key]?.toString()}
                    </TableCell>
                  );
                })}
                <TableCell sx={commonBodyCellSx}>
                  {PROTECTED_CHARACTERISTICS.includes(row.name) ? (
                    <MaterialTooltip
                      isShow
                      variant="dark"
                      placement="left"
                      title="System characteristics cannot be deleted"
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 20, color: "#5d7782", marginLeft: "7px" }} />
                    </MaterialTooltip>
                  ) : (
                    <MaterialTooltip isShow variant="dark" placement="left" title="Archive Characteristic">
                      <IconButton onClick={() => onDelete(row.id)}>
                        <ArchiveOutlinedIcon />
                      </IconButton>
                    </MaterialTooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Stack>
  );
};

const commonHeadCellSx: TableCellProps["sx"] = {
  backgroundColor: "#edf1f9",
  color: "#0a3142",
  fontSize: "16px",
  padding: "0 23px !important",
  verticalAlign: "middle",
  // minWidth: "140px",
  maxWidth: "240px",
  fontWeight: "normal",
  border: "none",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

const commonBodyCellSx: TableCellProps["sx"] = {
  color: "#013951",
  position: "relative",
  borderLeft: "4px solid transparent",
  padding: "0 23px",
  verticalAlign: "middle",
  fontSize: "15px",
  border: "none",
  borderBottom: "1px solid #bbc5c7",
  // minWidth: "140px",
  maxWidth: "240px"
};
